import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Dialog from "../../../../common/element/Dialog";
import BtnPrimary from "../../../../common/element/Buttons/btnPrimary";
import Select from "../../../../common/element/Select";
import ThreeDotLoader from "../../../../common/element/ButtonLoader/ThreeDotLoader";
import SweetAlertService from "../../../../common/SweetAlert/SweetAlert";
import {
  Toollist,
  addSessionMaster,
  updateSessionMaster,
  clearAddSessionMaster,
  clearUpdateSessionMaster
} from "../../../../redux/SessionMaster/sessionMasterSlice";
import { useDispatch, useSelector } from "react-redux";
import { getSupportedDocumentsList } from "../../../../redux/SupportedDocuments/supportedDocumentsSlice";
import {
  selectToolListData,
  selectAddSessionMasterData,
  selectUpdateSessionMasterData
} from "../../../../redux/SessionMaster/sessionMasterSelector";
import { selectSupportedDocumentsList } from "../../../../redux/SupportedDocuments/supportedDocumentsSelectors";
import { getAssignmentsList } from '../../../../redux/Assignment/assignmentsSlice';
import { selectAssignmentsList } from '../../../../redux/Assignment/assignmentsSelector';
import { selectRoleListingData } from '../../../../redux/auth/authSelector';
import { fetchRoleListing } from '../../../../redux/auth/authSlice';
import { formatToolName } from "../../../../common/Formatters.js/formatters";

// Define the IDs
const SOS_GROUP_ID = 4; // Science of Self
const PROSPECT_COMPANY_ID = 5; // Prospect Company
const SALES_TRAINING_GROUP_ID = 6; // Sales Training Companies

const AddEditSession = ({
  dialogOpen,
  onDialogClose,
  sessionData,
  isViewMode = false,
  onSuccess,
}) => {
  const location = useLocation();
  const isScienceOfSelfRoute = location.pathname.includes("scienceOfSelf");
  const isSalesTrainingRoute = location.pathname.includes("salesTrainingCompanies");
  const dispatch = useDispatch();
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const selectAssignmentsListData = useSelector(selectAssignmentsList);
  const toolData = useSelector(selectToolListData);
  const supportedDocumentsData = useSelector(selectSupportedDocumentsList);
  const roles = useSelector(selectRoleListingData);

  // Refs to track API calls and prevent duplicates
  const initialFetchDone = useRef(false);
  const prevAccessGroupIdsRef = useRef(null);
  const isInitialMount = useRef(true);

  // State to toggle between "For Session", "Prospect Company", "Science of Self", and "Sales Training Companies"
  const [accessType, setAccessType] = useState("session"); // 'session' | 'prospect' | 'sos' | 'stc'

  const [formData, setFormData] = useState({
    session_name: "",
    has_tool: false,
    tool_id: "",
    has_supported_doc: false,
    supportedDocIds: [],
    has_assignment: false,
    assignmentIds: [],
    access_group_ids: [],
  });

  const [formError, setFormError] = useState({});

  // Get "All" access group info
  const allAccessGroup = useMemo(() => {
    return roles?.find(role =>
      role.access_group_name?.toLowerCase() === 'all'
    );
  }, [roles]);

  const allGroupId = allAccessGroup?.access_group_id;

  // Resolve actual Sales Training group ID dynamically from API roles
  const salesTrainingGroupId = useMemo(() => {
    const found = roles?.find(role =>
      role.access_group_name?.toLowerCase().includes('sales training')
    );
    return found?.access_group_id ?? SALES_TRAINING_GROUP_ID;
  }, [roles]);

  // Get other group IDs (excluding "All", "Prospect Company", "Science of Self", and "Sales Training Companies" for the Standard Session checkboxes)
  const otherGroupIds = useMemo(() => {
    return roles
      ?.filter(role =>
        role.access_group_name?.toLowerCase() !== 'all' &&
        role.access_group_id !== PROSPECT_COMPANY_ID &&
        role.access_group_id !== SOS_GROUP_ID &&
        role.access_group_id !== salesTrainingGroupId
      )
      .map(role => role.access_group_id) || [];
  }, [roles, salesTrainingGroupId]);

  // Compute the API access group ID (only "All" if "All" is selected)
  const getApiAccessGroupId = useMemo(() => {
    if (formData.access_group_ids.includes(allGroupId)) {
      return [allGroupId];
    }
    return formData.access_group_ids;
  }, [formData.access_group_ids, allGroupId]);

  const toolOptions = useMemo(() => {
    return (toolData || []).map((item) => {
      const formattedName = formatToolName(item.tool);
      return {
        value: item.id,
        label: formattedName,
      };
    });
  }, [toolData]);

  const documentOptions = useMemo(() => {
    return (supportedDocumentsData || []).map((item) => ({
      value: item.supported_doc_id,
      label: item.name,
    }));
  }, [supportedDocumentsData]);

  const homeworkOptions = useMemo(() => {
    return (selectAssignmentsListData || []).map((item) => ({
      value: item.assignment_id,
      label: item.name || item.assignment_name,
    }));
  }, [selectAssignmentsListData]);

  // Fetch documents and assignments - memoized function
  const fetchDocumentsAndAssignments = useCallback((accessGroupIds) => {
    const params = {
      page: 1,
      limit: 0,
      search: "",
      access_group_id: accessGroupIds,
      archived: false, // Fetch only active documents
    };

    dispatch(getSupportedDocumentsList(params));
    dispatch(getAssignmentsList(params));
  }, [dispatch]);

  // Initial data fetch for tools and roles - ONLY ONCE
  useEffect(() => {
    if (dialogOpen && !initialFetchDone.current) {
      dispatch(Toollist());
      dispatch(fetchRoleListing({ include_prospect: true, include_science_of_self: true, include_sales_training: true }));
      initialFetchDone.current = true;
    }

    return () => {
      if (!dialogOpen) {
        dispatch(clearAddSessionMaster());
        dispatch(clearUpdateSessionMaster());
      }
    };
  }, [dialogOpen, dispatch]);

  // Fetch documents and assignments when access groups change
  useEffect(() => {
    if (!dialogOpen) return;

    // Create a stable string representation for comparison
    const currentAccessGroupStr = JSON.stringify(getApiAccessGroupId.sort());

    // Skip if access groups haven't changed
    if (prevAccessGroupIdsRef.current === currentAccessGroupStr) {
      return;
    }

    prevAccessGroupIdsRef.current = currentAccessGroupStr;

    if (isScienceOfSelfRoute) {
      fetchDocumentsAndAssignments([]);
    } else if (getApiAccessGroupId.length > 0) {
      fetchDocumentsAndAssignments(getApiAccessGroupId);
    } else {
      fetchDocumentsAndAssignments([]);
    }
  }, [dialogOpen, getApiAccessGroupId, fetchDocumentsAndAssignments, isScienceOfSelfRoute]);

  // Reset ref when dialog closes
  useEffect(() => {
    if (!dialogOpen) {
      prevAccessGroupIdsRef.current = null;
      isInitialMount.current = true;
      setAccessType("session"); // Reset default type strictly
    }
  }, [dialogOpen]);

  // Reset supported docs and assignments when access group changes (only for new sessions)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!sessionData && !isScienceOfSelfRoute && !isSalesTrainingRoute && dialogOpen) {
      setFormData(prev => ({
        ...prev,
        supportedDocIds: [],
        assignmentIds: [],
      }));
    }
  }, [getApiAccessGroupId, sessionData, isScienceOfSelfRoute, dialogOpen]);

  // Handle session data for edit/view mode
  useEffect(() => {
    if (!dialogOpen) return;

    if (sessionData && roles && roles.length > 0 && toolOptions.length >= 0) {
      // Get access group IDs from session data
      let accessGroupIds = [];
      let currentAccessType = "session";

      if (!isScienceOfSelfRoute && sessionData.access_for) {
        accessGroupIds = sessionData.access_for.map(access => access.access_group_id);

        // Determine Radio selection based on IDs
        if (accessGroupIds.includes(PROSPECT_COMPANY_ID)) {
          currentAccessType = "prospect";
          accessGroupIds = [PROSPECT_COMPANY_ID];
        } else if (accessGroupIds.includes(SOS_GROUP_ID)) {
          currentAccessType = "sos";
          accessGroupIds = [SOS_GROUP_ID];
        } else if (accessGroupIds.includes(salesTrainingGroupId)) {
          currentAccessType = "stc";
          accessGroupIds = [salesTrainingGroupId];
        } else {
          currentAccessType = "session";
          // If "All" is selected in the saved data, also check all other checkboxes
          if (allGroupId && accessGroupIds.includes(allGroupId)) {
            const allIds = new Set([allGroupId, ...otherGroupIds]);
            accessGroupIds = [...allIds];
          }
        }
      }

      setAccessType(currentAccessType);

      const mappedData = {
        session_name: sessionData.session_name || "",
        has_tool: sessionData.has_tool || false,
        tool_id: sessionData.tool_id
          ? toolOptions.find((opt) => opt.value === sessionData.tool_id) || ""
          : "",
        has_supported_doc: sessionData.has_supported_doc || false,
        supportedDocIds: sessionData.supported_documents
          ? sessionData.supported_documents.map(doc => ({
            value: doc.document_id,
            label: doc.document_name
          }))
          : [],
        has_assignment: sessionData.has_assignment || false,
        assignmentIds: sessionData.assignments
          ? sessionData.assignments.map(assignment => ({
            value: assignment.assignment_id,
            label: assignment.assignment_name
          }))
          : [],
        access_group_ids: accessGroupIds,
      };

      setFormData(mappedData);
    } else if (!sessionData && dialogOpen) {
      // ADD MODE: Ensure default is Session
      setAccessType("session");
      setFormData({
        session_name: "",
        has_tool: false,
        tool_id: "",
        has_supported_doc: false,
        supportedDocIds: [],
        has_assignment: false,
        assignmentIds: [],
        access_group_ids: [],
      });
    }

    setFormError({});
  }, [sessionData, dialogOpen, isScienceOfSelfRoute, toolOptions, allGroupId, otherGroupIds, roles]);

  const isEdit = !!sessionData?.session_id && !isViewMode;
  const isView = isViewMode;

  const handleInputChange = (field, value) => {
    if (isViewMode) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formError[field]) setFormError((prev) => ({ ...prev, [field]: "" }));
  };

  const handleToggle = (field, value) => {
    if (isViewMode) return;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "has_tool" && !value && { tool_id: "" }),
      ...(field === "has_supported_doc" && !value && {
        supportedDocIds: [],
      }),
      ...(field === "has_assignment" && !value && {
        assignmentIds: [],
      }),
    }));

    // Clear errors if toggled
    if (field === "has_tool" && value === true && formError.has_tool) {
      setFormError(prev => ({ ...prev, has_tool: "" }));
    }
  };

  // Handle Radio Button Change (For Session vs Prospect Company vs Science of Self)
  const handleAccessTypeChange = (type) => {
    if (isViewMode) return;
    setAccessType(type);

    if (type === "prospect") {
      // If prospect, set ID 5, clear docs/assignments
      setFormData(prev => ({
        ...prev,
        access_group_ids: [PROSPECT_COMPANY_ID],
        supportedDocIds: [],
        assignmentIds: [],
        has_supported_doc: false,
        has_assignment: false
      }));
    } else if (type === "sos") {
      // If Science of Self, set ID 4, Keep docs/assignments accessible
      setFormData(prev => ({
        ...prev,
        access_group_ids: [SOS_GROUP_ID],
        supportedDocIds: [],
        assignmentIds: [],
      }));
    } else if (type === "stc") {
      // If Sales Training Companies, set its ID, keep docs/assignments accessible
      setFormData(prev => ({
        ...prev,
        access_group_ids: [salesTrainingGroupId],
        supportedDocIds: [],
        assignmentIds: [],
      }));
    } else {
      // If session, clear IDs and let user select
      setFormData(prev => ({
        ...prev,
        access_group_ids: []
      }));
    }
    // Clear error
    if (formError.access_group_ids) setFormError((prev) => ({ ...prev, access_group_ids: "" }));
    // Clear module error if switching
    if (formError.has_tool || formError.tool_id) setFormError(prev => ({ ...prev, has_tool: "", tool_id: "" }));
  };

  const handleAccessChange = useCallback((groupId) => {
    if (isViewMode) return;

    setFormData((prev) => {
      let newAccessGroupIds = [...prev.access_group_ids];

      if (groupId === allGroupId) {
        if (newAccessGroupIds.includes(allGroupId)) {
          // Deselect All
          newAccessGroupIds = [];
        } else {
          // Select All: Include all standard groups
          newAccessGroupIds = [allGroupId, ...otherGroupIds];
        }
      } else {
        if (newAccessGroupIds.includes(groupId)) {
          // Uncheck a specific group
          newAccessGroupIds = newAccessGroupIds.filter((id) => id !== groupId);
          newAccessGroupIds = newAccessGroupIds.filter((id) => id !== allGroupId);
        } else {
          // Check a specific group
          newAccessGroupIds.push(groupId);

          // If we select a group, check if "All" condition is met
          const allOthersSelected = otherGroupIds.every(id =>
            newAccessGroupIds.includes(id)
          );

          if (allOthersSelected && allGroupId && !newAccessGroupIds.includes(allGroupId)) {
            newAccessGroupIds.push(allGroupId);
          }
        }
      }

      return {
        ...prev,
        access_group_ids: newAccessGroupIds,
      };
    });
  }, [isViewMode, allGroupId, otherGroupIds]);

  const validateForm = () => {
    const errors = {};
    if (!formData.session_name.trim())
      errors.session_name = "Session Name is required";

    // Check if Prospect Company is selected
    const isProspectSelected = formData.access_group_ids.includes(PROSPECT_COMPANY_ID);

    if (isProspectSelected) {
      // Validation for Prospect: Module is Mandatory
      if (!formData.has_tool) {
        errors.has_tool = "Module selection is mandatory for Prospect Company";
      } else if (!formData.tool_id) {
        errors.tool_id = "Select Tool is required";
      }
    } else {
      // Validation for Standard Session OR Science of Self: Module is Optional
      if (formData.has_tool && !formData.tool_id)
        errors.tool_id = "Select Tool is required";
    }

    // Only validate Docs/Assignments if NOT Prospect Company
    if (!isProspectSelected) {
      if (formData.has_supported_doc && (!formData.supportedDocIds || formData.supportedDocIds.length === 0))
        errors.supportedDocIds = "Select at least one Supported Document";
      if (formData.has_assignment && (!formData.assignmentIds || formData.assignmentIds.length === 0))
        errors.assignmentIds = "Select at least one Assignment";
    }

    if (!isScienceOfSelfRoute && !isSalesTrainingRoute && formData.access_group_ids.length === 0)
      errors.access_group_ids = "Select at least one Access Group";

    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setButtonDisabled(true);

      const payload = {
        session_name: formData.session_name,
        has_tool: formData.has_tool,
        tool_id: formData.has_tool && formData.tool_id ? formData.tool_id.value : null,
        has_supported_doc: formData.has_supported_doc,
        supportedDocIds: formData.has_supported_doc
          ? formData.supportedDocIds.map(doc => doc.value)
          : [],
        has_assignment: formData.has_assignment,
        assignmentIds: formData.has_assignment
          ? formData.assignmentIds.map(hw => hw.value)
          : [],
      };

      if (!isScienceOfSelfRoute && !isSalesTrainingRoute) {
        payload.access_group_ids = getApiAccessGroupId;
      } else if (isSalesTrainingRoute) {
        payload.access_group_ids = [salesTrainingGroupId];
      }

      try {
        if (isEdit) {
          await dispatch(updateSessionMaster({
            id: sessionData.session_id,
            data: payload
          })).unwrap();

          dispatch(clearUpdateSessionMaster());
        } else {
          await dispatch(addSessionMaster(payload)).unwrap();

          dispatch(clearAddSessionMaster());
        }

        SweetAlertService.showSuccess({
          title: 'Success!',
          message: `Session has been ${isEdit ? 'updated' : 'added'} successfully.`,
          onConfirm: () => {
            setButtonDisabled(false);
            onDialogClose();
            if (onSuccess) {
              onSuccess();
            }
          }
        });

      } catch (error) {
        console.error("Error submitting session:", error);
        setButtonDisabled(false);

        SweetAlertService.showError({
          title: 'Error!',
          message: error?.message || error || `Failed to ${isEdit ? 'update' : 'add'} session. Please try again.`,
          onConfirm: () => { }
        });
      }
    }
  };

  const hasAccessGroupSelected = isScienceOfSelfRoute || isSalesTrainingRoute || formData.access_group_ids.length > 0;

  // Logic to hide Supported Documents and Assignments if Prospect Company (ID 5) is selected
  // Science of Self (ID 4) will NOT trigger this, so it will show fields.
  const isProspectRestrictionActive = formData.access_group_ids.includes(PROSPECT_COMPANY_ID);

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={onDialogClose}
      width={1000}
      onRequestClose={onDialogClose}
      bodyOpenClassName="overflow-hidden"
    >
      {/* Header */}
      <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
        <div className="flex items-center">
          {/* ... SVG Icon ... */}
          <span className="h-[40px] w-10 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
            <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.74771 3.01889C4.58134 3.01889 5.25714 2.34309 5.25714 1.50944C5.25714 0.675801 4.58134 0 3.74771 0C2.91408 0 2.23828 0.675801 2.23828 1.50944C2.23828 2.34309 2.91408 3.01889 3.74771 3.01889Z" fill="#344054" />
              <path d="M4.90673 14.144H0.00195312V5.78491C0.00195312 5.38801 0.323554 5.06641 0.720455 5.06641C1.11736 5.06641 1.43896 5.38801 1.43896 5.78491V12.707H4.90673C5.30363 12.707 5.62523 13.0286 5.62523 13.4255C5.62523 13.8224 5.30363 14.144 4.90673 14.144Z" fill="#344054" />
              <path d="M7.15688 17.9867C6.60133 17.9867 6.15098 17.5363 6.15098 16.9807V13.2776L2.96342 12.4889C2.5145 12.378 2.19922 11.975 2.19922 11.5126V4.58648C2.19922 4.03093 2.64958 3.58057 3.20512 3.58057C3.76067 3.58057 4.21102 4.03093 4.21102 4.58648V10.7248L7.39858 11.5135C7.8475 11.6244 8.16278 12.0274 8.16278 12.4898V16.9805C8.16278 17.5363 7.71243 17.9867 7.15688 17.9867Z" fill="#344054" />
              <path d="M16.2706 3.30681C17.1093 3.30681 17.7892 2.62689 17.7892 1.78817C17.7892 0.949449 17.1093 0.269531 16.2706 0.269531C15.4319 0.269531 14.752 0.949449 14.752 1.78817C14.752 2.62689 15.4319 3.30681 16.2706 3.30681Z" fill="#344054" />
              <path d="M20.0005 14.2705H15.0955C14.6986 14.2705 14.377 13.9489 14.377 13.552C14.377 13.1551 14.6986 12.8335 15.0955 12.8335H18.5635V5.91138C18.5635 5.51448 18.8851 5.19287 19.282 5.19287C19.6789 5.19287 20.0005 5.51448 20.0005 5.91138V14.2705Z" fill="#344054" />
              <path d="M20 13.5518H14.9785V17.7907H20V13.5518Z" fill="#344054" />
              <path d="M12.8446 17.9693C12.3684 17.9693 11.9824 17.5833 11.9824 17.1071V12.6164C11.9824 12.2201 12.2526 11.8746 12.6374 11.7795L15.9342 10.9638V4.7128C15.9342 4.23657 16.3202 3.85059 16.7964 3.85059C17.2726 3.85059 17.6586 4.23657 17.6586 4.7128V11.6386C17.6586 12.035 17.3884 12.3804 17.0036 12.4756L13.7068 13.2912V17.1071C13.7068 17.5833 13.3211 17.9693 12.8446 17.9693Z" fill="#344054" />
              <path d="M5.02146 13.4707H0V17.7096H5.02146V13.4707Z" fill="#344054" />
              <path d="M10.0911 6.15819C9.53961 6.15819 9.01798 5.98057 8.5817 5.64459C8.48744 5.57188 8.46991 5.43651 8.54262 5.34225C8.61504 5.24827 8.75012 5.23016 8.84496 5.30316C9.20479 5.5805 9.63589 5.72737 10.0911 5.72737C11.2177 5.72737 12.134 4.81084 12.134 3.68422C12.134 2.5576 11.2177 1.64107 10.0911 1.64107C8.96452 1.64107 8.04829 2.5576 8.04829 3.68422C8.04829 4.15987 8.21527 4.62288 8.51848 4.98817C8.59464 5.07956 8.58199 5.21579 8.49031 5.29166C8.39863 5.36783 8.26269 5.35518 8.18653 5.2635C7.81952 4.82118 7.61719 4.26046 7.61719 3.68422C7.61719 2.31991 8.72684 1.20996 10.0911 1.20996C11.4554 1.20996 12.5651 2.31991 12.5651 3.68422C12.5651 5.04852 11.4554 6.15819 10.0911 6.15819Z" fill="#344054" />
              <path d="M10.8611 4.4821H9.86553C9.70659 4.4821 9.57812 4.35335 9.57812 4.1947V2.7459C9.57812 2.58725 9.70659 2.4585 9.86553 2.4585C10.0245 2.4585 10.1529 2.58725 10.1529 2.7459V3.9073H10.8611C11.02 3.9073 11.1485 4.03605 11.1485 4.1947C11.1485 4.35335 11.02 4.4821 10.8611 4.4821Z" fill="#344054" />
            </svg>
          </span>
          <div>
            <h5 className="mb-0 font-lato not-italic font-medium text-[16px] leading-[150%] text-[#101828]">
              {isView ? "View Session" : isEdit ? "Edit Session" : "Add Session"}
            </h5>
          </div>
        </div>

        <button
          onClick={onDialogClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></svg>
        </button>
      </div>

      {/* Form Body */}
      <div className="px-6 pb-5">
        {/* Session Name */}
        <div className="flex mb-5">
          <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
            Session Name*
          </label>
          <div className="w-3/4 pl-2">
            <input
              className={`h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${isView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              value={formData.session_name}
              placeholder="Session Name"
              onChange={(e) => handleInputChange("session_name", e.target.value)}
              disabled={isViewMode}
            />
            {formError.session_name && !isViewMode && (
              <p className="text-xs text-red-600 mt-1">
                {formError.session_name}
              </p>
            )}
          </div>
        </div>

        {/* Access For - Dynamic from API */}
        {!isScienceOfSelfRoute && (
          <div className="flex mb-5 ">
            {/* Left labels column: stacked Session Type and Access For (when applicable) */}
            <div className="w-1/4">
              <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] block">
                Session Type*
              </label>
              {/* Keep Access For label visible in left column when 'session' is selected */}
              {accessType === "session" && (
                <label className="font-medium text-gray-700 text-sm block mt-3">
                  Access For*
                </label>
              )}
            </div>

            {/* Right controls column: radios on top, checkboxes below */}
            <div className="w-3/4 pl-2">
              <div className="flex flex-col gap-3">
                {/* Radio Buttons Row */}
                <div className="flex items-center gap-6">
                  {/* Option 1: For Session */}
                  <label className="flex items-center gap-2 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] cursor-pointer">
                    <input
                      type="radio"
                      name="access_type"
                      checked={accessType === "session"}
                      onChange={() => handleAccessTypeChange("session")}
                      disabled={isViewMode}
                      className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 rounded-lg"
                    />
                    Company Session
                  </label>

                  {/* Option 2: Prospect Company */}
                  {roles?.find(role => role.access_group_id === PROSPECT_COMPANY_ID) && (
                    <label className="flex items-center gap-2 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] cursor-pointer">
                      <input
                        type="radio"
                        name="access_type"
                        checked={accessType === "prospect"}
                        onChange={() => handleAccessTypeChange("prospect")}
                        disabled={isViewMode}
                        className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 rounded-lg"
                      />
                      Prospect Company
                    </label>
                  )}

                  {/* Option 3: Science of Self */}
                  {roles?.find(role => role.access_group_id === SOS_GROUP_ID) && (
                    <label className="flex items-center gap-2 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] cursor-pointer">
                      <input
                        type="radio"
                        name="access_type"
                        checked={accessType === "sos"}
                        onChange={() => handleAccessTypeChange("sos")}
                        disabled={isViewMode}
                        className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 rounded-lg"
                      />
                      Science of Self
                    </label>
                  )}

                  {/* Option 4: Sales Training Companies */}
                  {roles?.find(role => role.access_group_id === salesTrainingGroupId) && (
                    <label className="flex items-center gap-2 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] cursor-pointer">
                      <input
                        type="radio"
                        name="access_type"
                        checked={accessType === "stc"}
                        onChange={() => handleAccessTypeChange("stc")}
                        disabled={isViewMode}
                        className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 rounded-lg"
                      />
                      Sales Training Companies
                    </label>
                  )}
                </div>

                {/* Show sub-checkboxes ONLY if "For Session" is selected and render below */}
                {accessType === "session" && (
                  <div className="mt-3">
                    <div className="flex gap-6 flex-wrap">
                      {roles && roles.length > 0 ? (
                        roles
                          .filter(role => role.access_group_id !== PROSPECT_COMPANY_ID && role.access_group_id !== SOS_GROUP_ID && role.access_group_id !== salesTrainingGroupId)
                          .map((role) => (
                            <label key={role.access_group_id} className="flex items-center gap-2 font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054]">
                              <input
                                type="checkbox"
                                checked={formData.access_group_ids.includes(role.access_group_id)}
                                onChange={() => handleAccessChange(role.access_group_id)}
                                disabled={isViewMode}
                                className={`w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 ${isViewMode ? "cursor-not-allowed" : "cursor-pointer"}`}
                              />
                              {role.access_group_name}
                            </label>
                          ))
                      ) : (
                        <span className="text-sm text-gray-500">Loading access groups...</span>
                      )}
                    </div>

                    {formError.access_group_ids && <p className="text-xs text-red-600 mt-1">{formError.access_group_ids}</p>}

                    {/* Warning text appears at the bottom */}
                    {!hasAccessGroupSelected && !isViewMode && (
                      <p className="text-xs text-[#FF8E1C] mt-1">
                        Please select access group(s) first to see available documents and assignments.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Module Toggle */}
        <div className="flex mb-5 ">
          <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
            Select Tools{isProspectRestrictionActive && "*"}
          </label>
          <div className="w-3/4 pl-2">
            <div className="flex mb-4 cstm-option">
              {["Yes", "No"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => handleToggle("has_tool", opt === "Yes")}
                  disabled={isViewMode}
                  className={`px-4 py-2 font-Lato not-italic font-medium text-[14px] leading-[150%] text-[#1D2939] border text-sm ${formData.has_tool === (opt === "Yes")
                    ? "bg-[#FFF4E5] border-gray-300"
                    : "bg-white border-gray-300 "
                    } ${isViewMode ? "cursor-not-allowed opacity-70" : ""}`}
                >
                  {opt}
                </button>
              ))}
            </div>

            {/* Specific Error for Module Toggle when Prospect is selected */}
            {formError.has_tool && !isViewMode && (
              <p className="text-xs text-red-600 mb-2">
                {formError.has_tool}
              </p>
            )}

            {formData.has_tool && (
              <Select
                placeholder="Select Tool"
                options={toolOptions}
                value={formData.tool_id}
                onChange={(opt) => handleInputChange("tool_id", opt)}
                isDisabled={isViewMode}
              />
            )}
            {formError.tool_id && !isViewMode && (
              <p className="text-xs text-red-600 mt-1">
                {formError.tool_id}
              </p>
            )}
          </div>
        </div>

        {/* HIDE Supported Documents and Homework if Prospect Company is selected */}
        {!isProspectRestrictionActive && (
          <>
            {/* Supported Documents - Multi-select */}
            <div className="flex mb-5 ">
              <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                Select Supported Documents
              </label>
              <div className="w-3/4 pl-2 ">
                <div className="flex  mb-4 cstm-option">
                  {["Yes", "No"].map((opt) => (
                    <button
                      key={opt}
                      onClick={() =>
                        handleToggle("has_supported_doc", opt === "Yes")
                      }
                      disabled={isViewMode || !hasAccessGroupSelected}
                      className={`px-4 py-2 font-Lato not-italic font-medium text-[14px] leading-[150%] text-[#1D2939] border text-sm  ${formData.has_supported_doc === (opt === "Yes")
                        ? "bg-[#FFF4E5] border-gray-300"
                        : "bg-white border-gray-300 "
                        } ${(isViewMode || !hasAccessGroupSelected) ? "cursor-not-allowed opacity-70" : ""}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {formData.has_supported_doc && (
                  <Select
                    placeholder="Select Supported Documents"
                    options={documentOptions}
                    value={formData.supportedDocIds}
                    onChange={(opt) =>
                      handleInputChange("supportedDocIds", opt)
                    }
                    isMulti
                    isDisabled={isViewMode || !hasAccessGroupSelected}
                  />
                )}
                {formError.supportedDocIds && !isViewMode && (
                  <p className="text-xs text-red-600 mt-1">
                    {formError.supportedDocIds}
                  </p>
                )}
              </div>
            </div>

            {/* Homework Activity - Multi-select */}
            <div className="flex mb-5 ">
              <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                Select Assignments
              </label>
              <div className="w-3/4 pl-2">
                <div className="flex mb-4 cstm-option">
                  {["Yes", "No"].map((opt) => (
                    <button
                      key={opt}
                      onClick={() =>
                        handleToggle("has_assignment", opt === "Yes")
                      }
                      disabled={isViewMode || !hasAccessGroupSelected}
                      className={`px-4 py-2 font-Lato not-italic font-medium text-[14px] leading-[150%] text-[#1D2939] border text-sm ${formData.has_assignment === (opt === "Yes")
                        ? "bg-[#FFF4E5] border-gray-300"
                        : "bg-white border-gray-300 "
                        } ${(isViewMode || !hasAccessGroupSelected) ? "cursor-not-allowed opacity-70" : ""}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {formData.has_assignment && (
                  <Select
                    placeholder="Select Assignments"
                    options={homeworkOptions}
                    value={formData.assignmentIds}
                    onChange={(opt) =>
                      handleInputChange("assignmentIds", opt)
                    }
                    isMulti
                    isDisabled={isViewMode || !hasAccessGroupSelected}
                  />
                )}
                {formError.assignmentIds && !isViewMode && (
                  <p className="text-xs text-red-600 mt-1">
                    {formError.assignmentIds}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer Buttons */}
      {!isViewMode ? (
        <div className="flex dailog-footer">
          <button
            className="mr-1.5 whiteBtnShadow btn border border-gray-100 block w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-medium"
            onClick={onDialogClose}
            style={{ backgroundColor: "#FFF4E5" }}
          >
            Cancel
          </button>
          <BtnPrimary
            disabled={buttonDisabled}
            onClick={handleSubmit}
            className="ml-1.5"
            style={{ backgroundColor: "#FF8E1C", borderColor: "#FF8E1C" }}
          >
            {buttonDisabled ? (
              <ThreeDotLoader />
            ) : isEdit ? (
              "Update Session"
            ) : (
              "Add Session"
            )}
          </BtnPrimary>
        </div>
      ) : (
        <div className="flex dailog-footer">
          <button
            className="whiteBtnShadow btn border border-gray-100 block w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-medium"
            onClick={onDialogClose}
          >
            Close
          </button>
        </div>
      )}
    </Dialog>
  );
};

export default AddEditSession;